import {USER_KEYS} from "@/api/userApi";
import EditIcon from "@/assets/colorable/edit.svg?react";
import Button from "@/components/Common/Button";
import PasswordField from "@/components/Common/forms/PasswordField";
import {useZodState} from "@/hooks/useZodState";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import type {TError} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import {Fragment, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useToggle} from "react-use";
import {z} from "zod";

const ChangePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, "passwordIsRequired"),
    password: z.string().min(8, "passwordAtLeast8Charaters"),
    passwordConfirmation: z.string().min(8, "passwordAtLeast8Charaters"),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    error: "passwordsNotMatching",
    path: ["passwordConfirmation"],
  });

const ChangePasswordSection: FC<{
  variant?: "collapsible" | "inline";
}> = ({variant = "collapsible"}) => {
  const {t} = useTranslation("common");
  const [isOpened, toggleIsOpened] = useToggle(false);
  const [state, setValue, validate, setState] = useZodState(ChangePasswordSchema, {
    oldPassword: "",
    password: "",
    passwordConfirmation: "",
  });

  const {mutate: changePassword, isPending} = useMutation({
    meta: {successMessage: t("passwordSuccessfullyChanged")},
    mutationKey: USER_KEYS.mUserPasswordResetRequest,
    mutationFn: () =>
      client.post(USER_KEYS.mUserPasswordResetRequest[0], {old_password: state.oldPassword, password: state.password}),
    onSuccess: () => {
      setState(s => {
        s.oldPassword = "";
        s.password = "";
        s.passwordConfirmation = "";
      });
      toggleIsOpened(false);
    },
    onError: (e: TError) => {
      setState(s => {
        s.errors.overall = e.response?.data.detail === "invalid_password" ? "invalidPassword" : "somethingWentWrong";
      });
    },
  });

  const fields = [
    {
      error: state.errors.oldPassword ? t(state.errors.oldPassword as "passwordIsRequired") : null,
      label: t("currentPassword"),
      onChange: (value: string) => setValue("oldPassword", value),
      value: state.oldPassword,
    },
    {
      error: state.errors.password ? t(state.errors.password as "passwordAtLeast8Charaters") : null,
      label: t("newPassword"),
      onChange: (value: string) => setValue("password", value),
      value: state.password,
    },
    {
      error: state.errors.passwordConfirmation
        ? t(state.errors.passwordConfirmation as "passwordAtLeast8Charaters")
        : null,
      label: t("confirmNewPassword"),
      onChange: (value: string) => setValue("passwordConfirmation", value),
      value: state.passwordConfirmation,
    },
  ];

  const renderFields = () =>
    fields.map((field, index) => (
      <Fragment key={field.label}>
        {index > 0 && <div className="border-neutral-grey-light-dark border-t" />}
        <div className="flex items-center justify-between gap-6">
          <span className="text-neutral-black text-sm font-medium">{field.label}</span>
          <div className="w-60 shrink-0">
            <PasswordField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 pr-10 text-sm"
              error={field.error}
              onChange={field.onChange}
              value={field.value}
            />
          </div>
        </div>
      </Fragment>
    ));

  const renderOverallError = () =>
    state.errors.overall && (
      <span className="text-danger-medium-red text-sm">{t(state.errors.overall as "invalidPassword")}</span>
    );

  if (variant === "inline") {
    return (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          {renderFields()}
          {renderOverallError()}
        </div>
        <Button
          buttonStyle="Gradient"
          className="h-10 w-fit px-5 text-sm"
          isLoading={isPending}
          onClick={() => validate() && changePassword()}>
          {t("Change password")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      {!isOpened ? (
        <>
          <label htmlFor="password" className={clsxm("text-neutral-grey-dark text-sm font-medium")}>
            {t("yourPassword")}
          </label>
          <div
            id="password"
            className="border-primary-light-solid bg-neutral-grey-lightest flex w-full flex-row items-center justify-between rounded-md border px-3 py-2">
            <span className="text-base">xxxxxxxxxxxx</span>
            <Button buttonStyle="Blue" className="h-10 gap-2" onClick={toggleIsOpened}>
              <EditIcon className="text-white" />
              {t("Change password")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <label htmlFor="password" className={clsxm("text-neutral-grey-dark text-sm font-medium")}>
            {t("yourPassword")}
          </label>
          <div
            id="password"
            className="border-primary-light-solid bg-neutral-grey-lightest flex w-full flex-col gap-3 rounded-md border px-3 py-2">
            {renderFields()}
            <div className="flex w-full justify-between">
              <Button
                onClick={() => toggleIsOpened(false)}
                className="border-neutral-grey-light h-14 w-48"
                buttonStyle={"White"}>
                {t("cancel")}
              </Button>
              <Button
                onClick={() => validate() && changePassword()}
                isLoading={isPending}
                className="h-14 w-48"
                buttonStyle={"Gradient"}>
                {t("Change password")}
              </Button>
            </div>

            {renderOverallError()}
          </div>
        </>
      )}
    </div>
  );
};

export default ChangePasswordSection;
